import { useCallback } from "react";
import {
  DiscardNoteEditCell,
  DiscardReasonEditCell,
  HarvestDateEditCell,
  StatusEditCell,
  TubWeightEditCell,
} from "./HarvestEntryEditors.jsx";
import { STATUS } from "./harvestEntryConstants.js";

export function useHarvestEntryEditors({
  ensurePendingDate,
  move,
  setDefaultHarvestDateISO,
  getEffectiveStatus,
  getBackendStatus,
  isRowDiscarded,
  showWeight,
  getGridPopupContainer,
  dropdownZIndex,
  harvestConfig,
  rowsRef,
  setIsEditorDirty,
}) {
  const renderDateEditCell = useCallback(
    ({ row, onRowChange, onClose, rowIdx, column }) => (
      <HarvestDateEditCell
        row={row}
        onRowChange={onRowChange}
        onClose={onClose}
        rowIdx={rowIdx}
        column={column}
        move={move}
        setDefaultHarvestDateISO={setDefaultHarvestDateISO}
        getPopupContainer={() => document.body}
        dropdownZIndex={dropdownZIndex}
      />
    ),
    [dropdownZIndex, move, setDefaultHarvestDateISO],
  );

  const renderNumberEditCell = useCallback(
    ({ row, onRowChange, onClose, rowIdx, column }) => {
      const key = column.key;

      const savedValue =
        key === "pendingYield" ? (row.yield ?? 0) : (row.weight ?? 0);

      const currentValue =
        row[key] !== null && row[key] !== undefined ? row[key] : savedValue;

      return (
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          value={
            currentValue === null || currentValue === undefined
              ? ""
              : String(currentValue)
          }
          autoFocus
          style={{
            width: "100%",
            height: 34,
            padding: "0 8px",
            textAlign: "center",
            boxSizing: "border-box",
            outline: "none",
            border: "0",
            boxShadow: "none",
            background: "transparent",
          }}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^\d-]/g, "");
            const base = ensurePendingDate({ ...row, pendingUnharvest: false });

            let nextRow;
            if (raw === "") {
              if (key === "pendingYield") {
                const backendYield = row.yield ?? 0;
                const clearsDiscard = backendYield !== -1;
                nextRow = {
                  ...base,
                  [key]: null,
                  ...(clearsDiscard
                    ? { pendingPloughedReasons: null, pendingPloughedNote: null }
                    : {}),
                };
              } else {
                nextRow = { ...base, [key]: null };
              }
            } else {
              const n = parseInt(raw, 10);
              const next = Number.isFinite(n) ? n : null;

              if (next !== null && next === savedValue) {
                nextRow = { ...base, [key]: null };
              } else if (key === "pendingYield" && next !== null && next !== -1) {
                nextRow = {
                  ...base,
                  [key]: next,
                  pendingPloughedReasons: null,
                  pendingPloughedNote: null,
                };
              } else {
                nextRow = { ...base, [key]: next };
              }
            }

            onRowChange(nextRow);
            if (rowsRef) {
              rowsRef.current = rowsRef.current.map((r) =>
                r.id === row.id ? { ...r, ...nextRow } : r,
              );
            }
            setIsEditorDirty?.(true);
          }}
          onBlur={() => onClose(true)}
          onKeyDown={(e) => {
            const k = e.key;
            if (
              k === "Enter" ||
              k === "ArrowDown" ||
              k === "ArrowUp" ||
              k === "ArrowLeft" ||
              k === "ArrowRight"
            ) {
              e.preventDefault();
              onClose(true);
              setTimeout(() => move({ rowIdx, idx: column.idx }, k, column.idx), 0);
            }
          }}
        />
      );
    },
    [ensurePendingDate, move, rowsRef, setIsEditorDirty],
  );

  const renderStatusEditCell = useCallback(
    ({ row, onRowChange, onClose }) => {
      const current = getEffectiveStatus(row);

      const apply = (next) => {
        const backend = getBackendStatus(row);

        if (next === backend && current !== backend) {
          onRowChange(
            {
              ...row,
              pendingUnharvest: false,
              pendingHarvestDate: null,
              pendingYield: null,
              pendingWeight: null,
              pendingTubWeight: null,
              pendingPloughedReasons: null,
              pendingPloughedNote: null,
            },
            true,
          );
          return;
        }

        if (next === STATUS.PLANTED) {
          const wasHarvested =
            row.is_harvested ||
            (row.pendingHarvestDate !== null &&
              row.pendingHarvestDate !== undefined) ||
            (row.pendingYield !== null && row.pendingYield !== undefined) ||
            (row.pendingWeight !== null && row.pendingWeight !== undefined) ||
            isRowDiscarded(row);

          if (wasHarvested) {
            onRowChange(
              {
                ...row,
                pendingUnharvest: true,
                pendingHarvestDate: null,
                pendingYield: null,
                pendingWeight: null,
                pendingTubWeight: null,
                pendingPloughedReasons: null,
                pendingPloughedNote: null,
              },
              true,
            );
            return;
          }

          onRowChange(
            {
              ...row,
              pendingUnharvest: false,
              pendingHarvestDate: null,
              pendingYield: null,
              pendingWeight: null,
              pendingTubWeight: null,
              pendingPloughedReasons: null,
              pendingPloughedNote: null,
            },
            true,
          );
          return;
        }

        if (next === STATUS.HARVESTED) {
          const base = ensurePendingDate({
            ...row,
            pendingUnharvest: false,
            pendingYield: null,
            pendingWeight: null,
            pendingPloughedReasons: null,
            pendingPloughedNote: null,
          });
          onRowChange(base, true);
          return;
        }

        if (next === STATUS.DISCARDED) {
          // Find the last reason used on any other row; fall back to first config option.
          const allRows = rowsRef?.current ?? [];
          let defaultReasonIds = null;
          for (const r of allRows) {
            if (r.id === row.id) continue;
            const ids =
              r.pendingPloughedReasons !== null && r.pendingPloughedReasons !== undefined
                ? r.pendingPloughedReasons
                : (r.ploughed_under_reasons ?? []).map((rr) => rr.id);
            if (ids.length > 0) defaultReasonIds = ids;
          }
          if (!defaultReasonIds) {
            const sorted = [...(harvestConfig?.discard_reasons ?? [])].sort(
              (a, b) => a.sort_order - b.sort_order,
            );
            if (sorted.length > 0) defaultReasonIds = [sorted[0].id];
          }

          const base = ensurePendingDate({
            ...row,
            pendingUnharvest: false,
            pendingYield: -1,
            pendingWeight: showWeight ? -1 : row.pendingWeight,
            pendingPloughedReasons: defaultReasonIds ?? null,
            pendingPloughedNote: null,
          });
          onRowChange(base, true);
        }
      };

      return (
        <StatusEditCell
          row={row}
          onRowChange={onRowChange}
          onClose={onClose}
          apply={apply}
          current={current}
          getPopupContainer={getGridPopupContainer}
          dropdownZIndex={dropdownZIndex}
        />
      );
    },
    [
      dropdownZIndex,
      ensurePendingDate,
      getBackendStatus,
      getEffectiveStatus,
      getGridPopupContainer,
      harvestConfig,
      isRowDiscarded,
      rowsRef,
      showWeight,
    ],
  );

  const renderTubWeightEditCell = useCallback(
    ({ row, onRowChange, onClose, rowIdx, column }) => (
      <TubWeightEditCell
        row={row}
        onRowChange={onRowChange}
        onClose={onClose}
        ensurePendingDate={ensurePendingDate}
        harvestConfig={harvestConfig}
        move={move}
        rowIdx={rowIdx}
        column={column}
        onTubWeightChange={(newVal) => {
          if (!rowsRef) return;
          rowsRef.current = rowsRef.current.map((r) =>
            r.id === row.id ? { ...r, pendingTubWeight: newVal } : r,
          );
          setIsEditorDirty?.(true);
        }}
      />
    ),
    [ensurePendingDate, harvestConfig, move, rowsRef, setIsEditorDirty],
  );

  const renderDiscardReasonEditCell = useCallback(
    ({ row, onRowChange, onClose }) => (
      <DiscardReasonEditCell
        row={row}
        onRowChange={onRowChange}
        onClose={onClose}
        harvestConfig={harvestConfig}
        getPopupContainer={getGridPopupContainer}
        dropdownZIndex={dropdownZIndex}
        ensurePendingDate={ensurePendingDate}
        showWeight={showWeight}
      />
    ),
    [dropdownZIndex, ensurePendingDate, getGridPopupContainer, harvestConfig, showWeight],
  );

  const renderDiscardNoteEditCell = useCallback(
    ({ row, onRowChange, onClose, rowIdx, column }) => {
      const buildNextRow = (note) => {
        const currentRow =
          rowsRef?.current?.find((r) => r.id === row.id) ?? row;
        const effectiveYield =
          currentRow.pendingYield !== null &&
          currentRow.pendingYield !== undefined
            ? currentRow.pendingYield
            : currentRow.yield;
        const isPlanted =
          effectiveYield !== -1 && !currentRow.pendingUnharvest;

        if (isPlanted && note) {
          const base = ensurePendingDate({
            ...currentRow,
            pendingUnharvest: false,
          });
          return {
            ...base,
            pendingYield: -1,
            ...(showWeight ? { pendingWeight: -1 } : {}),
            pendingPloughedNote: note,
          };
        }
        return { ...currentRow, pendingPloughedNote: note };
      };

      return (
        <DiscardNoteEditCell
          row={row}
          onRowChange={onRowChange}
          onClose={onClose}
          buildNextRow={buildNextRow}
          move={move}
          rowIdx={rowIdx}
          column={column}
          onNoteChange={(note) => {
            if (!rowsRef) return;
            const nextRow = buildNextRow(note);
            // Synchronous ref update so handleSaveVisible always reads latest
            rowsRef.current = rowsRef.current.map((r) =>
              r.id === row.id ? nextRow : r,
            );
            // Signal that a note is being typed so Save button enables.
            // We deliberately do NOT call setRows here — updating rows while the
            // editor is open causes react-data-grid to detect an external row
            // change and lose focus on the input.
            setIsEditorDirty?.(true);
          }}
        />
      );
    },
    [ensurePendingDate, move, rowsRef, setIsEditorDirty, showWeight],
  );

  return {
    renderDateEditCell,
    renderNumberEditCell,
    renderStatusEditCell,
    renderTubWeightEditCell,
    renderDiscardReasonEditCell,
    renderDiscardNoteEditCell,
  };
}
